import { getApiConfig } from '../config/apiConfig';
import { getTokenManager } from '../config/tokenManager';
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { ErrorFromResponse } from '../utils';
import { addLoggingInterceptors } from './logger';
export const customerApiClient: AxiosInstance = axios.create({
  baseURL: getApiConfig().apiBaseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null,
  },
});

customerApiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    config.baseURL = getApiConfig().apiBaseUrl;
    try {
      const businessId =
        config.params?.get?.('businessId') ??
        config.params.businessId ??
        config.data?.businessId;

      // Check for Customer access token
      if (businessId) {
        const { accessToken } = getTokenManager().getCustomerTokens(businessId);
        if (accessToken && config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`;
          return config;
        } else {
          getTokenManager().logger.warn(
            `[CustomerClient] No access token found for businessId: ${businessId}`
          );
        }
      } else {
        getTokenManager().logger.warn(
          `[CustomerClient] No businessId found in request to ${config.url}`,
          {
            params: config.params,
            data: config.data,
            method: config.method,
          }
        );
      }
    } catch (error) {
      getTokenManager().logger.error('Failed to get auth token:', { error });
    }

    return config;
  },
  (error) => {
    getTokenManager().logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
customerApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data?.errorCode === 'AUTH.INVALID_REFRESH_TOKEN') {
      const businessId =
        response.config.params?.get?.('businessId') ??
        response.config.params?.businessId ??
        response.config.data?.businessId;
      if (businessId) {
        getTokenManager().removeCustomerTokens(businessId);
      }
      getTokenManager().onAuthError();
      return Promise.reject(new ErrorFromResponse(response.data));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (
        originalRequest.url?.includes('/refresh') ||
        error.response?.data?.errorCode === 'AUTH.INVALID_REFRESH_TOKEN'
      ) {
        const businessId =
          originalRequest.params?.get?.('businessId') ??
          originalRequest.params?.businessId ??
          originalRequest.data?.businessId;
        if (businessId) {
          getTokenManager().removeCustomerTokens(businessId);
        }
        if (!originalRequest.url?.includes('/auth/')) {
          getTokenManager().onAuthError();
        }
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      try {
        // Try to refresh JWT token first
        // Need businessId to get correct refresh token
        const businessId =
          originalRequest.params?.get?.('businessId') ??
          originalRequest.params.businessId ??
          originalRequest.data?.businessId;

        if (businessId) {
          const { refreshToken } =
            getTokenManager().getCustomerTokens(businessId);

          if (refreshToken) {
            try {
              const { data: refreshResponse } =
                await customerApiClient.post<any>('/customer/auth/refresh', {
                  refreshToken,
                });

              if (!refreshResponse.success) {
                throw new ErrorFromResponse(refreshResponse);
              }

              const {
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
              } = refreshResponse.data ?? {};

              if (!newAccessToken || !newRefreshToken) {
                throw new Error(
                  'Failed to get new tokens from refresh response'
                );
              }

              getTokenManager().setCustomerTokens(businessId, {
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
              });

              // Update the authorization header with the new access token.
              // Note: customerApiClient uses default headers, but we might be using request config headers
              // originalRequest header update:
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

              return customerApiClient(originalRequest); // Retry
            } catch (refreshError) {
              getTokenManager().logger.error('Failed to refresh auth token:', {
                error: refreshError,
              });
              // Refresh failed, clear tokens
              getTokenManager().removeCustomerTokens(businessId);
            }
          }
        }
      } catch (refreshError) {
        getTokenManager().logger.error('Failed to refresh auth token:', {
          error: refreshError,
        });
      }
    }

    // Handle other errors
    // if (error.response?.status >= 500) {
    //   getTokenManager().logger.error('Server error:', error.response.data);
    // } else if (error.response?.status >= 400) {
    //   getTokenManager().logger.error('Client error:', error.response.data);
    // }

    return Promise.reject(error);
  }
);

addLoggingInterceptors(customerApiClient, 'customerApiClient');
